import React, { useMemo, useState } from "react";
import TowerDayNotice from "./TowerDayNotice";
import { statusText, statusTone } from "./model/shipmentStatus";
import Card from "../ui/Card";
import Input from "../ui/Input";
import TowerDriverDialog from "./driver/TowerDriverDialog";
import { driversForPlace, driversNear, findShipments, listedShipments, NEARBY_KM } from "./model/locateDrivers";
import { useTowerDay } from "./hooks/useTowerDay";
import type { TowerDashboard, TowerDriverDetail, TowerListedShipment } from "../../types/controlTower";
import { activateOnKey } from "./model/towerShared";
import { formatKm } from "./model/towerFormat";

// Pestaña "Ubicar choferes": quién hizo una localidad o zona en el día, y
// quién estuvo cerca de un envío buscado por tracking.
export default function TowerLocateDrivers({
  data,
  today,
  token,
  operationalDateLabel,
  countLabel,
}: {
  data: TowerDashboard;
  today: string;
  token: string | null;
  operationalDateLabel: string;
  countLabel: string;
}) {
  const [query, setQuery] = useState("");
  const [targetId, setTargetId] = useState<string | null>(null);
  // Se guarda el id: el detalle abierto toma cada actualización del día.
  const [openDriverId, setOpenDriverId] = useState<string | null>(null);
  const { day, setDay, data: source, error, retry, pastNote } = useTowerDay(today, data, token);

  const drivers = useMemo(() => source?.drivers || [], [source]);
  const openDriver = openDriverId ? drivers.find((d) => d.id === openDriverId) || null : null;
  const listed = useMemo(() => listedShipments(drivers, source?.depot?.shipments || []), [drivers, source]);
  const places = driversForPlace(drivers, query);
  const found = findShipments(listed, query);
  const target: TowerListedShipment | null = targetId ? listed.find((s) => s.id === targetId) || null : null;
  const nearby = target ? driversNear(drivers, target) : [];
  const dayLabel = day === today ? "hoy" : `el ${day.split("-").reverse().join("/")}`;
  const zonesOf = (driver: TowerDriverDetail) => driver.zones.filter((z) => z !== "Sin clasificar").join(" / ") || "—";

  return (
    <div className="tower-locate">
      <Card className="tower-board">
        <div className="tower-board-heading">
          <h2>Ubicar choferes de zona</h2>
        </div>
        <div className="tower-toolbar">
          <div className="tower-search">
            <Input
              label="Buscar"
              type="search"
              placeholder="Localidad, zona o tracking"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setTargetId(null);
              }}
            />
          </div>
          <label>
            Día
            <input
              type="date"
              className="tower-date"
              value={day}
              max={today}
              onChange={(e) => {
                setDay(e.target.value);
                setTargetId(null);
              }}
            />
          </label>
        </div>
        <TowerDayNotice error={error} onRetry={retry} pastNote={pastNote} />
        {!source && !error && (
          <p className="tower-muted" role="status">
            Cargando…
          </p>
        )}

        {target && (
          <section className="tower-locate-section" aria-label="Choferes cerca del envío">
            <h3>
              Choferes cerca de {target.tracking}{" "}
              <span className="tower-muted">
                hasta {NEARBY_KM} km · {dayLabel}
              </span>
            </h3>
            <p className="tower-muted">
              {target.address || "Sin dirección"} · {target.locality || "Sin localidad"} · {target.statusLabel} ·{" "}
              {target.assignedTo}
            </p>
            <div className="tower-table-scroll">
              <table className="tower-table">
                <thead>
                  <tr>
                    <th>Chofer</th>
                    <th>Distancia</th>
                    <th>Misma localidad</th>
                    <th>{countLabel}</th>
                    <th>En camino</th>
                    <th>Zonas</th>
                  </tr>
                </thead>
                <tbody>
                  {nearby.map((n) => (
                    <tr
                      key={n.driver.id}
                      className="tower-driver-row"
                      tabIndex={0}
                      onClick={() => setOpenDriverId(n.driver.id)}
                      onKeyDown={activateOnKey(() => setOpenDriverId(n.driver.id))}
                    >
                      <td>
                        <strong>{n.driver.name}</strong>
                        {n.isCurrentDriver && <small>Chofer actual</small>}
                      </td>
                      <td>{formatKm(n.nearestKm)}</td>
                      <td>{n.sameLocality}</td>
                      <td>{n.driver.counts.total}</td>
                      <td>{n.driver.counts.inTransit}</td>
                      <td>{zonesOf(n.driver)}</td>
                    </tr>
                  ))}
                  {nearby.length === 0 && (
                    <tr>
                      <td colSpan={6} className="tower-empty">
                        Ningún chofer cerca {dayLabel}.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <button type="button" className="tower-map-filter" onClick={() => setTargetId(null)}>
              ← Volver
            </button>
          </section>
        )}

        {source && !target && query.trim() && (
          <>
            {found.length > 0 && (
              <section className="tower-locate-section" aria-label="Envíos con ese tracking">
                <div className="tower-table-scroll">
                  <table className="tower-table">
                    <thead>
                      <tr>
                        <th>Tracking / cliente</th>
                        <th>Destino</th>
                        <th>Estado actual</th>
                        <th>Asignado a</th>
                      </tr>
                    </thead>
                    <tbody>
                      {found.map((s) => (
                        <tr
                          key={s.id}
                          className="tower-driver-row"
                          tabIndex={0}
                          onClick={() => setTargetId(s.id)}
                          onKeyDown={activateOnKey(() => setTargetId(s.id))}
                        >
                          <td>
                            <strong>{s.tracking}</strong>
                            <small>{s.client}</small>
                          </td>
                          <td>
                            {s.address || "Sin dirección"}
                            <small>{s.locality || "Sin localidad"}</small>
                          </td>
                          <td>
                            <span className={`tower-state ${statusTone(s)}`}>{statusText(s)}</span>
                          </td>
                          <td>{s.assignedTo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
            <section className="tower-locate-section" aria-label="Choferes que hicieron el lugar">
              <div className="tower-table-scroll">
                <table className="tower-table">
                  <thead>
                    <tr>
                      <th>Chofer</th>
                      <th>{countLabel} ahí</th>
                      <th>Entregados ahí</th>
                      <th>En camino ahí</th>
                      <th>Última entrega ahí</th>
                      <th>Zonas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {places.map((m) => (
                      <tr
                        key={m.driver.id}
                        className="tower-driver-row"
                        tabIndex={0}
                        onClick={() => setOpenDriverId(m.driver.id)}
                        onKeyDown={activateOnKey(() => setOpenDriverId(m.driver.id))}
                      >
                        <td>
                          <strong>{m.driver.name}</strong>
                        </td>
                        <td>{m.shipments.length}</td>
                        <td className="tower-green">{m.delivered}</td>
                        <td>{m.inTransit}</td>
                        <td>{m.lastVisit ? m.lastVisit.slice(11) || m.lastVisit : "—"}</td>
                        <td>{zonesOf(m.driver)}</td>
                      </tr>
                    ))}
                    {places.length === 0 && (
                      <tr>
                        <td colSpan={6} className="tower-empty">
                          Sin choferes en esa localidad o zona {dayLabel}.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
        {source && !target && !query.trim() && (
          <p className="tower-empty">Buscá una localidad, una zona o un tracking.</p>
        )}
      </Card>
      {openDriver && (
        <TowerDriverDialog
          key={`${day}:${openDriver.id}`}
          driver={openDriver}
          date={day}
          operationalDateLabel={operationalDateLabel}
          countLabel={countLabel}
          initialMapStatus="all"
          onClose={() => setOpenDriverId(null)}
        />
      )}
    </div>
  );
}
